// AJAX tables (the generic table system). A container marked data-n-table swaps its content
// for the server's table partial when you search, sort or page, without a full reload:
//
//   <div id="role-table-container" data-n-table data-n-table-search="#search-form">
//
// Requests carry X-Requested-With: XMLHttpRequest, which makes BaseTableController return the
// partial. After each swap the server's toasts are shown and 'tableManagerReinitialized' fires
// on document, for page scripts that decorate rows. window.TableManager stays available for
// scripts that construct one by hand.
import { $, $$ } from './dom';
import { consumeServerToasts, showToast } from './toast';

export class TableManager {
	private container: HTMLElement | null;
	private form: HTMLFormElement | null;
	private busy = false;
	private timer = 0;

	constructor(options: { containerSelector?: string; searchFormSelector?: string } = {}) {
		this.container = $(options.containerSelector ?? '#table-container');
		this.form = $<HTMLFormElement>(options.searchFormSelector ?? '#search-form');
		if (!this.container) return;
		this.container.dataset.nTableBound = '';
		this.container.addEventListener('click', (e) => {
			const link = (e.target as Element).closest<HTMLAnchorElement>('a[data-ajax="true"]');
			if (!link) return;
			e.preventDefault();
			if (link.getAttribute('aria-disabled') === 'true') return;
			this.load(link.href, link.getAttribute('data-ajax-update'));
		});
		if (this.form) {
			this.form.addEventListener('submit', (e) => {
				e.preventDefault();
				this.search();
			});
			const input = this.form.querySelector<HTMLInputElement>('[name="search"]');
			input?.addEventListener('input', () => {
				clearTimeout(this.timer);
				this.timer = window.setTimeout(() => this.search(), 350);
			});
			this.form.addEventListener('change', (e) => {
				if ((e.target as Element).matches('select')) this.search();
			});
		}
	}

	search(): Promise<void> {
		if (!this.form) return Promise.resolve();
		const params = new URLSearchParams(new FormData(this.form) as unknown as Record<string, string>);
		params.set('page', '1');
		const exportLink = this.form.querySelector<HTMLAnchorElement>('[data-n-table-export]');
		if (exportLink) {
			const url = new URL(exportLink.href, location.href);
			const query = new URLSearchParams(params);
			query.delete('page');
			query.delete('pageSize');
			url.search = query.toString();
			exportLink.href = url.toString();
		}
		return this.load(`${this.form.action}?${params}`);
	}

	async load(url: string, target?: string | null): Promise<void> {
		const into = (target && $(target)) || this.container;
		if (!into || this.busy) return;
		this.busy = true;
		into.setAttribute('aria-busy', 'true');
		try {
			const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' } });
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			into.innerHTML = await response.text();
			consumeServerToasts();
			document.dispatchEvent(new CustomEvent('tableManagerReinitialized', { detail: { container: into } }));
		} catch {
			showToast('The table could not be loaded. Please try again.', 'error');
		} finally {
			into.removeAttribute('aria-busy');
			this.busy = false;
		}
	}
}

export function initTables(): void {
	window.TableManager = TableManager;
	for (const el of $$('[data-n-table]')) {
		if (el.hasAttribute('data-n-table-bound')) continue;
		new TableManager({ containerSelector: '#' + el.id, searchFormSelector: el.dataset.nTableSearch ?? '#search-form' });
	}
}
